import {
	useCallback, useContext, useEffect, useState,
} from 'react';
import { useDispatch, useSelector, batch } from 'react-redux';
import { track } from '@amplitude/analytics-browser';
import Socket from '../connection/socket';
import { ROOM_STATES } from '../stores/reducers/settings';
import { Shot, baseShot, baseShotData } from '../stores/reducers/shots';
import type { PartialShot, Shot as ShowboardShot } from '@brandlive/showboard';
import { GreenroomState as GRS, GreenroomState } from '../stores/types/types';
import { AmpEvent } from '../types/amplitude';
import { SHOT_MODAL_PAGES } from '../components/modal/templates/shot-builder/shot-builder';
import { SHOT_MODAL_TABS } from '../stores/types/tabs';
import { MODAL_TEMPLATES } from '../stores/reducers/modal';
import {
	duplicateShot,
	editShot,
	setPlayingShot,
	setShotModalShot,
	setShotTabShotId,
} from '../stores/actions/shots';
import { setActiveTab, setRightDrawerOpen } from '../stores/actions/tabs';
import { TABS } from '../stores/types/tabs';
import { setPresentedMediaVideoState } from '../stores/actions/media';
import { setClearShot } from '../stores/actions/settings';
import {
	getPresentedAutoDurationMediaId,
	getPresentedAutoDurationTimeLeft,
} from '../utils/media';
import { setModalState } from '../stores/actions/modal';
import { DuplicateShot, clearShotStartTimestampEntity } from '../connection/shots';
import useShots from '../contexts/shots/provider';
import useAutoplay from './use-autoplay';
import { showAlert } from '../components/general-components/alert-service';
import { IMG_TYPES, VID_TYPES } from '../types/constants';
import { editOverlayDraft, removeAllAnimatedOverlays, resetAllShotOverlays } from '../connection/graphics';
import { loadDraftContent } from '../stores/actions/generate-content';
import { UpsertMediaDraft } from '../connection/media';
import { getDraftEntities } from '../connection/generate-content';
import { EntityType } from '../types/enums';
import { setDraftMedia } from '../stores/actions/media';
import { setDraftOverlays } from '../stores/actions/graphics';
import { GraphicInterface } from '../stores/reducers/graphics';
import { MediaItem, PresentedMediaItem } from '../stores/reducers/media';
import { ShowboardAlert } from '@brandlive/showboard';
import { SocketContext } from '../contexts/socket';
import { useVideoMachine } from '../contexts/video-machine';
import { getEffectiveShotDuration, useEffectiveShotDuration } from './use-effective-shot-duration';

export const useOpenShotModal = () => {
	const roomState = useSelector((state: GRS) => state.MeetingReducer.roomState);
	const shotModalShot = useSelector((state: GRS) => state.ShotsReducer.shotModalShot);
	const graphics = useSelector((state: GRS) => state.GraphicsReducer.graphics);
	const media = useSelector((state: GRS) => state.MediaReducer.media);
	const token = useSelector((state: GRS) => state.SessionReducer.token);

	const getGraphicsOnShot = useCallback((shot: Shot) => {
		if (!graphics || !shot) return [];
		const shotGraphics = shot.shot_data.shotGraphics || [];
		return graphics.filter((graphic) => shotGraphics.includes(graphic.id ?? 0));
	}, [graphics]);

	const getMediaOnShot = useCallback((shot: Shot) => {
		if (!media || !shot) return [];
		const shotMedia = shot.shot_data.shotFeeds
			.map((feed) => feed.media)
			.filter((media) => media !== null);
		return media.filter((media) => shotMedia.includes(media.id ?? 0));
	}, [media]);

	const dispatch = useDispatch();

	const handleOpenShotModal = async (shot: Shot, socket?: Socket | null) => {
		socket?.setUpEditListeners();
		if (roomState === ROOM_STATES.SHOWTIME) {
			track(AmpEvent.shotEditModalOpened);

			// get overlays and media that already have existing draft entities
			const currentDraftOverlays: GraphicInterface[] = await getDraftEntities(token ?? '', shot.meeting, EntityType.overlay);
			const currentDraftMedia: MediaItem[] = await getDraftEntities(token ?? '', shot.meeting, EntityType.media);
			dispatch(setDraftOverlays(currentDraftOverlays));
			dispatch(setDraftMedia(currentDraftMedia));

			batch(() => {
				// add draft overlays for the shot that don't have existing draft entities
				const currentDraftOverlayIds = currentDraftOverlays.map((over) => over.id);
				const currentDraftMediaIds = currentDraftMedia.map((media) => media.id);
				const newDraftOverlays = getGraphicsOnShot(shot).filter((over) => !currentDraftOverlayIds.includes(over.id ?? 0));
				newDraftOverlays.forEach((over) => editOverlayDraft(token ?? '', over, true));
				const newDraftMedia = getMediaOnShot(shot).filter((media) => !currentDraftMediaIds.includes(media.id ?? 0));
				newDraftMedia.forEach((media) => UpsertMediaDraft(shot.meeting, media, true));

				dispatch(loadDraftContent(token ?? '', shot.meeting));
				dispatch(setShotModalShot(shot));
				dispatch(setModalState({
					modalOpen: true,
					modalTemplate: MODAL_TEMPLATES.SHOT_BUILDER,
					modalData: {
						socket,
						modalPage: (shot.shot_id || shotModalShot.shot_id)
							? SHOT_MODAL_PAGES.EDITING
							: SHOT_MODAL_PAGES.LAYOUT,
						modalTab: (shot.shot_id || shotModalShot.shot_id)
							? SHOT_MODAL_TABS.MEDIA
							: SHOT_MODAL_TABS.FEEDS,
						modalGraphicsLoading: newDraftOverlays.length > 0,
					},
				}));
			});
		}
	};

	return handleOpenShotModal;
};

export const useDuplicateShot = () => {
	const roomState = useSelector((state: GRS) => state.MeetingReducer.roomState);
	const token = useSelector((state: GRS) => state.SessionReducer.token);
	const session = useSelector((state: GRS) => state.SessionReducer.session);

	const dispatch = useDispatch();
	const { handleSelectShot } = useShots();

	const handleDuplicateShot = async (shot: Shot, socket?: Socket | null) => {
		if (token && session) {
			const duplicatedShot = await DuplicateShot(
				token,
				shot.shot_id,
				shot.shot_data.order + 1,
				session,
			);
			dispatch(duplicateShot(duplicatedShot));

			if (roomState === ROOM_STATES.EDITING && socket) {
				handleSelectShot(duplicatedShot.shot_id);
				dispatch(setShotTabShotId(duplicatedShot.shot_id));
			}
		}
	};

	return handleDuplicateShot;
};

export const useToggleAutoplay = () => {
	const token = useSelector((state: GreenroomState) => state.SessionReducer.token);
	const meeting = useSelector((state: GreenroomState) => state.SessionReducer.meeting);
	const shotStart = useSelector(
		(state: GreenroomState) => state.ShotsReducer.shotStartTimestampEntity?.entityValue?.start,
	);
	const useNewShotThumbs = useSelector(
		(state: GreenroomState) => state.SettingsReducer.featureFlags.use_new_shot_thumbs,
	);

	const dispatch = useDispatch();
	const { currentShot } = useShots();
	const { getShotToggleAutoplay } = useAutoplay();

	const handleToggleAutoplay = (shot: Shot): void => {
		if (!token || !shot) return;

		const canChangeTimer = !!shotStart
			&& shot.shot_id === currentShot.shot_id;

		const toggleAutoplayShot = getShotToggleAutoplay(
			shot,
			canChangeTimer,
			shotStart,
		);

		if (toggleAutoplayShot) {
			dispatch(editShot(
				token,
				toggleAutoplayShot,
				meeting?.meeting_id,
				meeting?.channel,
				useNewShotThumbs,
			));
		}
	};

	return handleToggleAutoplay;
};

export const useAutoplayOnScheduledStream = () => {
	const token = useSelector((s: GRS) => s.SessionReducer.token);
	const scheduledStream = useSelector((s: GRS) => s.MeetingReducer.scheduledStream);
	const media = useSelector((s: GRS) => s.MediaReducer.media);
	const shotStart = useSelector(
		(s: GRS) => s.ShotsReducer.shotStartTimestampEntity?.entityValue?.start,
	);

	const { currentShot } = useShots();
	const { getShotToggleAutoplay } = useAutoplay();

	const handleAutoplayOnScheduledStream = (shot: Shot): Shot => {
		const {
			shot_id: shotId,
			shot_data: {
				autoplay,
				shotDuration,
				shotFeeds,
			},
		} = shot;

		const startTimeNumber = scheduledStream?.startTime?.valueOf() ?? 0;
		const mediaItem = media.find((m) => m.id === shotFeeds[0].media);

		const isImage = IMG_TYPES.includes(mediaItem?.type ?? '');
		const isVideo = VID_TYPES.includes(mediaItem?.type ?? '');

		const mediaData = mediaItem?.media_data;

		const videoAndNotLooped = !isImage && !mediaData?.loop;
		const couldBeAutoDuration = autoplay && (shotDuration || videoAndNotLooped);

		if (couldBeAutoDuration
			|| startTimeNumber <= Date.now() // scheduled stream is in the past
			|| shotFeeds.length !== 1 // shot is not a 1-up
			|| shotFeeds?.[0]?.feedType !== 'media' // shot is not a media shot
			|| !(isImage || isVideo) // shot is not a media type
			|| !token
			|| !shot
		) {
			return shot;
		}

		// if the shot is currently playing, and the user is editing the duration
		const canChangeTimer = !!shotStart && shotId === currentShot.shot_id;

		const isAutoDuration = mediaItem
			&& (!mediaItem?.num_pages || mediaItem?.num_pages === 1)
			&& isVideo
			&& mediaData
			&& !(mediaData?.loop ?? false);

		if (isAutoDuration || shotDuration) {
			const toggleAutoplayShot = getShotToggleAutoplay(
				shot,
				canChangeTimer,
				shotStart,
				true,
				!isAutoDuration,
				true,
			);

			if (toggleAutoplayShot) {
				showAlert({
					message: 'Shot set to autoplay',
					description: 'A scheduled stream is pending',
					type: 'autoplay-notification',
					autoWidth: true,
				});

				return toggleAutoplayShot;
			}
		}

		showAlert({
			message: 'Please input a duration',
			description: 'The shot will skip during a scheduled stream',
			type: 'duration-notification',
			autoWidth: true,
		});

		return shot;
	};

	return handleAutoplayOnScheduledStream;
};

export const useOpenDeleteModal = () => {
	const shots = useSelector((state: GRS) => state.ShotsReducer.shots);

	const dispatch = useDispatch();

	const handleOpenDeleteModal = (shot: Shot, socket?: Socket | null) => {
		if (shots && shots.length === 1) {
			showAlert({
				message: 'You can’t do that',
				description: 'You need at least 1 shot in the showboard',
				duration: 5000,
				type: 'neutral',
			});
			return;
		}

		batch(() => {
			dispatch(setShotModalShot(shot));
			dispatch(setModalState({
				modalOpen: true,
				modalTemplate: MODAL_TEMPLATES.DELETE_SHOT_CONFIRMATION,
				modalData: { socket },
			}));
		});
	};

	return handleOpenDeleteModal;
};

export const useOpenBulkDeleteModal = () => {
	const shots = useSelector((state: GRS) => state.ShotsReducer.shots);
	const dispatch = useDispatch();

	const handleOpenBulkDeleteModal = (shotIds: number[]) => {
		if (shots.length - shotIds.length < 1) {
			showAlert({
				message: "You can't do that",
				description: "You need at least 1 shot in the showboard",
				duration: 5000,
				type: "neutral",
			});
			return;
		}

		dispatch(setModalState({
			modalOpen: true,
			modalTemplate: MODAL_TEMPLATES.BULK_DELETE_SHOTS_CONFIRMATION,
			modalData: { shotIds },
		}));
	};

	return handleOpenBulkDeleteModal;
};

export const AUTOPLAY_SECOND_BUFFER = 3;

export const useTotalShotDuration = () => {
	const shots = useSelector((state: GRS) => state.ShotsReducer.shots);
	const media = useSelector((state: GRS) => state.MediaReducer.media);
	const distributedShotDurations = useSelector((state: GRS) => state.SegmentsReducer.distributedShotDurations);
	const useSegments = useSelector((state: GRS) => state.SettingsReducer.featureFlags.use_segments);
	const { getShotDuration } = useShots();

	const [totalDuration, setTotalDuration] = useState<number>(0);

	useEffect(() => {
		const getTotalDuration = async () => {
			let duration = 0;
			for (let i = 0; i < shots.length; i += 1) {
				const shot = shots[i];
				const shotDuration = getEffectiveShotDuration(shot, useSegments, distributedShotDurations);
				const newDuration = shot.shot_data.autoDuration && shot.shot_data.shotFeeds[0].media
					// eslint-disable-next-line no-await-in-loop
					? await getShotDuration(shot.shot_data.shotFeeds[0].media)
					: shotDuration;
				duration += newDuration;
			}
			setTotalDuration(duration);
		};
		getTotalDuration();
	}, [shots, media]);

	return totalDuration;
};

export const useSelectShot = () => {
	const shots = useSelector((state: GRS) => state.ShotsReducer.shots);
	const shotStartTimestampEntity = useSelector((state: GRS) => state.ShotsReducer.shotStartTimestampEntity?.entityValue?.start);
	const currentShotId = useSelector((state: GRS) => state.ShotsReducer.currentShotId);
	const shotTabShotId = useSelector((state: GRS) => state.ShotsReducer.shotTabShotId);
	const clearShot = useSelector((state: GRS) => state.SettingsReducer.clearShot);
	const token = useSelector((state: GRS) => state.SessionReducer.token);
	const meeting = useSelector((state: GRS) => state.SessionReducer.meeting);
	const presentedMedia = useSelector((state: GRS) => state.MediaReducer.presentedMedia);
	const animatedOverlays = useSelector((state: GRS) => state.GraphicsReducer.animatedOverlays);
	const roomState = useSelector((state: GRS) => state.MeetingReducer.roomState);

	const dispatch = useDispatch();
	const { handleSelectShot: selectShotInContext } = useShots();
	const { deleteShotAutoplay } = useAutoplay();

	const handleSelectShot = useCallback((
		targetShotId: number,
		socket?: Socket | null,
	) => {
		// targetShotId === -1 signals a show reset (prev from the first shot)
		if (targetShotId === -1) {
			const currentShot = shots.find((s: Shot) => s.shot_id === currentShotId);

			dispatch(setPlayingShot(null));

			if (token && meeting) {
				clearShotStartTimestampEntity(token, meeting.meeting);
			}

			presentedMedia.forEach((media: PresentedMediaItem) => {
				if (media.videoState) {
					dispatch(setPresentedMediaVideoState({
						mediaId: media.mediaId,
						videoState: {
							...media.videoState,
							paused: true,
							timeElapsed: -1.5,
							volume: media.videoState.volume ?? 1,
						},
						doEmitChanges: true,
					}));
				}
			});

			if (token && meeting) {
				resetAllShotOverlays(token, meeting.meeting);
				if (animatedOverlays?.length) {
					removeAllAnimatedOverlays(token, meeting.meeting);
				}
			}

			if (currentShot) {
				deleteShotAutoplay(currentShot, true);
			}
			return;
		}

		const shot = shots.find((s: Shot) => s.shot_id === targetShotId);
		if (!shot) return;

		const { shot_data: { autoplay, autoDuration } } = shot;
		const shotTabIsCurrentActiveShot = shotTabShotId === currentShotId;

		if (roomState === ROOM_STATES.EDITING) {
			selectShotInContext(shot.shot_id, true, socket);
			if (!shotTabIsCurrentActiveShot) {
				dispatch(setShotTabShotId(shot.shot_id));
			}
			return;
		}

		if (shotStartTimestampEntity && autoplay && !autoDuration) {
			deleteShotAutoplay(shot, true);
		}

		selectShotInContext(shot.shot_id, true, socket);

		if (!shotTabIsCurrentActiveShot) {
			dispatch(setShotTabShotId(shot.shot_id));
		}

		if (clearShot) {
			dispatch(setClearShot(false));
		}
	}, [
		shots,
		shotStartTimestampEntity,
		currentShotId,
		shotTabShotId,
		clearShot,
		token,
		meeting,
		presentedMedia,
		animatedOverlays,
		roomState,
		selectShotInContext,
		deleteShotAutoplay,
	]);

	return handleSelectShot;
};

export const useToggleClearShot = () => {
	const clearShot = useSelector((state: GRS) => state.SettingsReducer.clearShot);
	const currentShotId = useSelector((state: GRS) => state.ShotsReducer.currentShotId);
	const shotStartTimestampEntity = useSelector((state: GRS) => state.ShotsReducer.shotStartTimestampEntity?.entityValue?.start);
	const presentedMedia = useSelector((state: GRS) => state.MediaReducer.presentedMedia);
	const media = useSelector((state: GRS) => state.MediaReducer.media);

	const dispatch = useDispatch();
	const { currentShot } = useShots();
	const { setShotAutoplay } = useAutoplay();
	const handleSelectShot = useSelectShot();
	const shotDuration = useEffectiveShotDuration(currentShot);

	const handleToggleClearShot = useCallback(() => {
		batch(() => {
			const newPresentedMedia = presentedMedia
				.filter((presentedMediaItem) => presentedMediaItem.videoState)
				.map((presentedMediaItem) => {
					// eslint-disable-next-line no-param-reassign
					presentedMediaItem.videoState!.paused = !clearShot;
					return {
						mediaId: presentedMediaItem.mediaId,
						videoState: presentedMediaItem.videoState!,
						doEmitChanges: true,
					};
				});

			newPresentedMedia.forEach((presentedMediaItem) => {
				dispatch(setPresentedMediaVideoState(presentedMediaItem));
			});

			dispatch(setClearShot(!clearShot));

			if (clearShot) {
				let shotTimeLeft = shotDuration;

				if (currentShot?.shot_data?.autoDuration) {
					const autoDurationMediaId = getPresentedAutoDurationMediaId(
						currentShot,
						media,
						presentedMedia,
					);
					if (autoDurationMediaId) {
						shotTimeLeft = getPresentedAutoDurationTimeLeft(
							autoDurationMediaId,
							currentShot,
							presentedMedia,
						);
					}
				}

				// add timer when un-clearing shot
				// (will be deleted in Beehive when shot cleared)
				setShotAutoplay(currentShot, shotTimeLeft);

				// if shotStartTimestampEntity is null it means it's the first shot
				if (!shotStartTimestampEntity && currentShotId) {
					handleSelectShot(currentShotId);
				}
			}
		});
	}, [
		clearShot,
		currentShot,
		currentShotId,
		shotStartTimestampEntity,
		presentedMedia,
		media,
		shotDuration,
		dispatch,
		setShotAutoplay,
		handleSelectShot,
	]);

	return handleToggleClearShot;
};

export const useShowboardApp = () => {
	const shots = useSelector((state: GRS) => state.ShotsReducer.shots);
	const media = useSelector((state: GRS) => state.MediaReducer.media);
	const mediaPages = useSelector((state: GRS) => state.MediaReducer.mediaPages);
	const currentShotId = useSelector((state: GRS) => state.ShotsReducer.currentShotId);
	const shotStartTimestamp = useSelector((state: GRS) => state.ShotsReducer.shotStartTimestampEntity?.entityValue?.start);
	const session = useSelector((state: GRS) => state.SessionReducer.session);
	const roomState = useSelector((state: GRS) => state.MeetingReducer.roomState);
	const host = useSelector((state: GRS) => state.SessionReducer.host);
	const meeting = useSelector((state: GRS) => state.SessionReducer.meeting);
	const clearShot = useSelector((state: GRS) => state.SettingsReducer.clearShot);
	const connectedUsers = useSelector((state: GRS) => state.SessionReducer.connectedUsers);
	const invitations = useSelector((state: GRS) => state.InvitationsReducer.invitations);
	const webrtcID = useSelector((state: GRS) => state.SessionReducer.webrtcID);

	const dispatch = useDispatch();
	const { socket } = useContext(SocketContext);
	const { lockAutoPlay } = useShots();
	const { videoMachineState } = useVideoMachine();

	const handleOpenShotModal = useOpenShotModal();
	const onToggleAutoplayShot = useToggleAutoplay();
	const handleOpenDeleteModal = useOpenDeleteModal();
	const handleOpenBulkDeleteModal = useOpenBulkDeleteModal();
	const handleSelectShot = useSelectShot();
	const onToggleClearShot = useToggleClearShot();
	const { deleteShotAutoplay } = useAutoplay();

	const onSelectShot = (shotId: number) => {
		handleSelectShot(shotId, socket);
	};

	const onOpenShotModalShot = (shot: PartialShot) => {
		const fullShot: Shot = {
			...baseShot,
			...shot,
			shot_data: { ...baseShotData, ...shot.shot_data },
			shot_style: { ...baseShot.shot_style, ...shot.shot_style },
		};
		handleOpenShotModal(fullShot, socket);
	};

	const onOpenShotTab = (shotId: number) => {
		batch(() => {
			dispatch(setActiveTab(TABS.SHOTS));
			dispatch(setRightDrawerOpen(true));
			dispatch(setShotTabShotId(shotId));
		});
	};

	const onDeleteShot = (shot: Shot) => {
		handleOpenDeleteModal(shot, socket);
	};

	const onDeleteSegment = (segmentId: number, shots: ShowboardShot[]) => {
		const isLastSegment = shots.every(shot => shot.segment_id === segmentId);
		if (isLastSegment) {
			showAlert({
				message: 'Cannot delete segment',
				description: 'You need at least 1 shot in the showboard',
				duration: 5000,
				type: 'neutral',
			});
			return;
		}
		dispatch(setModalState({
			modalOpen: true,
			modalTemplate: MODAL_TEMPLATES.DELETE_SEGMENT_CONFIRMATION,
			modalData: { segmentId },
		}));
	};

	const onBulkDeleteShots = (shotIds: number[]) => {
		handleOpenBulkDeleteModal(shotIds);
	};

	const onCancelAutoplay = () => {
		const currentShot = shots.find(s => s.shot_id === currentShotId);
		if (!currentShot) return;
		deleteShotAutoplay(currentShot);
		socket?.sendMessage('temp-auto-play', { shotId: currentShot.shot_id });
	};

	const onAlert = (alert: ShowboardAlert) => {
		if (!alert.data?.message && !alert.data?.description) return;
		showAlert({
			message: alert.data.message,
			description: alert.data.description,
			duration: 5000,
			type: alert.type,
		});
	};

	const greenroomState = {
		currentShotId,
		shotStartTimestamp,
		shots,
		media,
		mediaPages,
		session: session ?? undefined,
		roomState,
		presenterOverride: !host,
		socket: socket ?? undefined,
		meeting: meeting ?? undefined,
		lockAutoPlay,
		clearShot,
		videoMachineState,
		connectedUsers,
		invitations,
		webrtcID: webrtcID ?? undefined,
	};

	const callbacks = {
		dispatch,
		onSelectShot,
		onCancelAutoplay,
		onToggleClearShot,
		onOpenShotModalShot,
		onOpenShotTab,
		onToggleAutoplayShot,
		onDeleteShot,
		onBulkDeleteShots,
		onDeleteSegment,
		onAlert,
	};

	return { greenroomState, callbacks };
};